import React from "react";
import { SSEStage, STAGE_LABELS } from "../types";

interface LoadingStagesProps {
  stage: SSEStage;
}

const STEPS: SSEStage[] = ["thinking", "sql", "data", "summary"];

const ThinkIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const CodeIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
);

const DbIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
  </svg>
);

const SparkleIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

const STEP_ICONS: Record<SSEStage, React.ReactNode> = {
  thinking: <ThinkIcon />,
  sql:      <CodeIcon />,
  data:     <DbIcon />,
  summary:  <SparkleIcon />,
  // Terminal stages have no step icon -- this component renders the progress
  // strip during a query, and "clarify" ends the stream rather than advancing it.
  idle: null, error: null, done: null, clarify: null,
};

const LoadingStages: React.FC<LoadingStagesProps> = ({ stage }) => {
  if (stage === "idle" || stage === "done" || stage === "error" || stage === "clarify") return null;

  const currentIdx = STEPS.indexOf(stage);

  return (
    <div className="flex items-center gap-1.5 py-1">
      {STEPS.map((step, i) => {
        const done   = i < currentIdx;
        const active = i === currentIdx;
        return (
          <React.Fragment key={step}>
            <div className="flex items-center gap-1.5">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center
                  transition-all duration-300
                  ${done   ? "bg-green-500 text-white"
                  : active ? "bg-blue-500 text-white animate-pulse"
                  :          "bg-gray-100 text-gray-400"}`}
              >
                {done ? <CheckIcon /> : STEP_ICONS[step]}
              </div>
              <span
                className={`text-xs font-medium hidden sm:block transition-colors
                  ${active ? "text-blue-600"
                  : done   ? "text-green-600"
                  :          "text-gray-400"}`}
              >
                {STAGE_LABELS[step]}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`flex-1 h-px max-w-[36px] transition-colors duration-500
                  ${i < currentIdx ? "bg-green-300" : "bg-gray-200"}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default LoadingStages;
